import { apiClient } from '@/lib/api-client';
import {
  Formulario, FormularioCampo, FormularioResposta, FormularioArquivoEnviado, FormularioDashboard, FormularioStatus, ApiResponse,
} from '@/types';

export const formularioService = {
  async getAll(params?: { status?: string; is_modelo?: boolean; empresa_id?: string; lixeira?: boolean }): Promise<ApiResponse<Formulario[]>> {
    try {
      const query = new URLSearchParams();
      if (params?.status) query.set('status', params.status);
      if (params?.is_modelo !== undefined) query.set('is_modelo', String(params.is_modelo));
      if (params?.empresa_id) query.set('empresa_id', params.empresa_id);
      if (params?.lixeira) query.set('lixeira', 'true');
      const qs = query.toString();
      return await apiClient.get<ApiResponse<Formulario[]>>(`/formularios${qs ? `?${qs}` : ''}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar formulários', data: [] };
    }
  },

  async getById(id: string): Promise<ApiResponse<Formulario>> {
    try {
      return await apiClient.get<ApiResponse<Formulario>>(`/formularios/${id}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar formulário', data: {} as Formulario };
    }
  },

  async create(data: Partial<Formulario>): Promise<ApiResponse<Formulario>> {
    try {
      return await apiClient.post<ApiResponse<Formulario>>('/formularios', data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao criar formulário', data: {} as Formulario };
    }
  },

  async update(id: string, data: Partial<Formulario>): Promise<ApiResponse<Formulario>> {
    try {
      return await apiClient.put<ApiResponse<Formulario>>(`/formularios/${id}`, data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao atualizar formulário', data: {} as Formulario };
    }
  },

  async delete(id: string): Promise<ApiResponse<boolean>> {
    try {
      return await apiClient.delete<ApiResponse<boolean>>(`/formularios/${id}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao excluir formulário', data: false };
    }
  },

  async restaurar(id: string): Promise<ApiResponse<Formulario>> {
    try {
      return await apiClient.post<ApiResponse<Formulario>>(`/formularios/${id}/restaurar`, {});
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao restaurar formulário', data: {} as Formulario };
    }
  },

  async excluirPermanente(id: string): Promise<ApiResponse<boolean>> {
    try {
      return await apiClient.delete<ApiResponse<boolean>>(`/formularios/${id}/permanente`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao excluir formulário', data: false };
    }
  },

  async duplicar(id: string): Promise<ApiResponse<Formulario>> {
    try {
      return await apiClient.post<ApiResponse<Formulario>>(`/formularios/${id}/duplicar`, {});
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao duplicar formulário', data: {} as Formulario };
    }
  },

  async gerarLink(id: string): Promise<ApiResponse<{ share_token: string; status: FormularioStatus }>> {
    try {
      return await apiClient.post<ApiResponse<{ share_token: string; status: FormularioStatus }>>(`/formularios/${id}/link`, {});
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao gerar link', data: { share_token: '', status: 'rascunho' } };
    }
  },

  async desativarLink(id: string): Promise<ApiResponse<boolean>> {
    try {
      return await apiClient.delete<ApiResponse<boolean>>(`/formularios/${id}/link`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao desativar link', data: false };
    }
  },
};

function apiPublicBaseUrl(): string {
  return (window as any).__ENV__?.VITE_API_URL || import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
}

export const formularioPublicoService = {
  async getByToken(token: string): Promise<ApiResponse<Formulario>> {
    try {
      const res = await fetch(`${apiPublicBaseUrl()}/public/formularios/${token}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      return data;
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao carregar formulário', data: {} as Formulario };
    }
  },

  async submeter(token: string, payload: { valores: Record<string, unknown> }): Promise<ApiResponse<{ id: string }>> {
    try {
      const res = await fetch(`${apiPublicBaseUrl()}/public/formularios/${token}/respostas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      return data;
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao enviar resposta', data: { id: '' } };
    }
  },
};

export const formularioCampoService = {
  async create(formularioId: string, data: Partial<FormularioCampo>): Promise<ApiResponse<FormularioCampo>> {
    try {
      return await apiClient.post<ApiResponse<FormularioCampo>>(`/formularios/${formularioId}/campos`, data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao criar campo', data: {} as FormularioCampo };
    }
  },

  async update(formularioId: string, campoId: string, data: Partial<FormularioCampo>): Promise<ApiResponse<FormularioCampo>> {
    try {
      return await apiClient.put<ApiResponse<FormularioCampo>>(`/formularios/${formularioId}/campos/${campoId}`, data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao atualizar campo', data: {} as FormularioCampo };
    }
  },

  async delete(formularioId: string, campoId: string): Promise<ApiResponse<boolean>> {
    try {
      return await apiClient.delete<ApiResponse<boolean>>(`/formularios/${formularioId}/campos/${campoId}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao excluir campo', data: false };
    }
  },

  async reordenar(formularioId: string, campoIds: string[]): Promise<ApiResponse<FormularioCampo[]>> {
    try {
      return await apiClient.patch<ApiResponse<FormularioCampo[]>>(`/formularios/${formularioId}/campos/reordenar`, { campo_ids: campoIds });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao reordenar campos', data: [] };
    }
  },

  async uploadArquivoResposta(formularioId: string, campoId: string, file: File): Promise<ApiResponse<FormularioArquivoEnviado>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      return await apiClient.postMultipart<ApiResponse<FormularioArquivoEnviado>>(
        `/formularios/${formularioId}/campos/${campoId}/upload-resposta`,
        formData
      );
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao enviar arquivo',
        data: {} as FormularioArquivoEnviado,
      };
    }
  },
};

export const formularioRespostaService = {
  async submeter(formularioId: string, valores: Record<string, unknown>): Promise<ApiResponse<{ id: string }>> {
    try {
      return await apiClient.post<ApiResponse<{ id: string }>>(`/formularios/${formularioId}/respostas`, { valores });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao enviar resposta', data: { id: '' } };
    }
  },

  async atualizar(formularioId: string, respostaId: string, valores: Record<string, unknown>): Promise<ApiResponse<{ id: string }>> {
    try {
      return await apiClient.put<ApiResponse<{ id: string }>>(`/formularios/${formularioId}/respostas/${respostaId}`, { valores });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao atualizar resposta', data: { id: '' } };
    }
  },

  async minhaResposta(formularioId: string): Promise<ApiResponse<FormularioResposta | null>> {
    try {
      return await apiClient.get<ApiResponse<FormularioResposta | null>>(`/formularios/${formularioId}/respostas/minha`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar sua resposta', data: null };
    }
  },

  async listar(formularioId: string): Promise<ApiResponse<FormularioResposta[]>> {
    try {
      return await apiClient.get<ApiResponse<FormularioResposta[]>>(`/formularios/${formularioId}/respostas`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar respostas', data: [] };
    }
  },

  async detalhe(formularioId: string, respostaId: string): Promise<ApiResponse<FormularioResposta>> {
    try {
      return await apiClient.get<ApiResponse<FormularioResposta>>(`/formularios/${formularioId}/respostas/${respostaId}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar resposta', data: {} as FormularioResposta };
    }
  },

  async dashboard(formularioId: string): Promise<ApiResponse<FormularioDashboard>> {
    try {
      return await apiClient.get<ApiResponse<FormularioDashboard>>(`/formularios/${formularioId}/dashboard`);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar dashboard',
        data: { total_respostas: 0, campos: [], respostas_por_dia: [] },
      };
    }
  },
};
